import React, { ComponentProps, useCallback, useMemo } from 'react'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import TextButton from 'components/Luxkit/Button/TextButton'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'

interface Props extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'children'> {
  rooms: Array<App.RoomOccupants>;
  checkIn?: string;
  roomType?: App.PackageRoomType;
  packageOptions: Array<OfferPackageOptionView>;
  isFLightSelected?: boolean;
  activeFilter?: OfferPageFilterType;
  onSubmit?: () => void;
  durationSelected?: boolean;
}

function AccommodationMobileViewOptionsButton(props: Props) {
  const {
    rooms,
    checkIn,
    roomType,
    packageOptions,
    isFLightSelected,
    activeFilter,
    onSubmit,
    onClick,
    durationSelected,
    ...rest
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)
  const viewOptionsLabel = useMemo(() => {
    if (!checkIn) return 'Check availability'
    return 'Select room'
  }, [checkIn])

  const onViewOptionsClick = useCallback(async(e: React.MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    if (invalidRoomSelection || !durationSelected) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'capacity',
        room: roomType,
        flights: isFLightSelected,
      })
      return
    }

    if (!checkIn) {
      e.preventDefault()
      offerPageDispatch({
        type: OfferPageActions.checkAvailability,
        filter: 'checkin',
        room: roomType,
        flights: isFLightSelected,
      })
      return
    }

    // Reset bedbank room id for Single Property pages
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: undefined })
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
  }, [onClick, invalidRoomSelection, durationSelected, checkIn, offerPageDispatch, roomType, isFLightSelected])

  return (
    <TextButton {...rest} kind="primary" onClick={onViewOptionsClick}>
      {viewOptionsLabel}
    </TextButton>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  packageOptions: getDurationPackageOptions(state, appState),
  isFLightSelected: state.flights,
  activeFilter: state.activeFilter,
  durationSelected: state.durationSelected,
}))(AccommodationMobileViewOptionsButton)
